import { ProgramFrameSource, type FrameComposition } from '@ohmyprime/segmenter';
import type { Logger } from '../core/services';

interface CanvasCaptureTrack extends MediaStreamTrack {
  requestFrame(): void;
}

/**
 * The program picture as a live video track. Frames are taken from the
 * renderer canvas synchronously, in the same task that drew them.
 */
export class ProgramOutput {
  readonly stream: MediaStream;
  readonly track: MediaStreamTrack;
  readonly mode: 'generator' | 'capture';
  /** Composed frames the track did not take: consumer backpressure, or a track that stopped accepting. */
  droppedFrames = 0;
  private writer: WritableStreamDefaultWriter<VideoFrame> | null = null;
  private captureTrack: CanvasCaptureTrack | null = null;
  private lastTimestampUs = -1;
  private closed = false;
  private failed = false;
  private sceneIdentity: string | undefined;
  private frameSource: ProgramFrameSource | null = null;
  /** performance.now() when the write in flight was issued, or -1 when none is pending. */
  private pendingSinceMs = -1;

  constructor(
    private canvas: HTMLCanvasElement,
    private readonly logger: Logger,
  ) {
    if (typeof MediaStreamTrackGenerator === 'function' && typeof VideoFrame === 'function') {
      const generator = new MediaStreamTrackGenerator({ kind: 'video' });
      this.writer = generator.writable.getWriter();
      this.track = generator;
      this.mode = 'generator';
      this.frameSource = new ProgramFrameSource(this.track);
    } else {
      // captureStream(0) only emits when requestFrame() is called, which pins output to composed frames.
      const captured = canvas.captureStream(0);
      this.captureTrack = captured.getVideoTracks()[0] as CanvasCaptureTrack;
      this.track = this.captureTrack;
      this.mode = 'capture';
    }
    this.stream = new MediaStream([this.track]);
  }

  get sourceGeneration(){return this.frameSource?.generation;}
  get sourceId(){return this.frameSource?.identity;}
  /** True when the track can no longer carry frames (it ended, or its sink rejected a frame). */
  get wedged(): boolean {
    return this.failed || this.track.readyState === 'ended';
  }

  /** Frames are taken from another canvas from now on. Only possible in generator mode. */
  setCanvas(canvas: HTMLCanvasElement): void {
    if (canvas !== this.canvas) this.frameSource?.replace();
    this.canvas = canvas;
  }

  /**
   * Publishes the canvas as the next frame. Call right after the program
   * picture was drawn. Returns false when the frame was not handed over.
   * `stallLimitMs` is how long one write may stay pending before the sink is
   * declared wedged (a writer that never settles looks like backpressure
   * otherwise, forever).
   */
  push(timeMs: number, stallLimitMs: number, sceneIdentity?: string, composition?: FrameComposition): boolean {
    if (sceneIdentity !== this.sceneIdentity) { this.sceneIdentity = sceneIdentity; this.frameSource?.replace(); }
    if (this.closed) return false;
    if (this.wedged) {
      this.droppedFrames++;
      return false;
    }
    if (this.captureTrack) {
      this.captureTrack.requestFrame();
      return true;
    }
    const writer = this.writer!;
    // Never queue: a consumer that is behind gets the next frame instead of a growing backlog.
    if (writer.desiredSize !== null && writer.desiredSize <= 0) {
      this.droppedFrames++;
      if (this.pendingSinceMs >= 0 && performance.now() - this.pendingSinceMs > stallLimitMs) {
        this.failed = true;
        this.logger.log('error', 'render', `The program track has not accepted a frame for ${Math.round(stallLimitMs)} ms`);
      }
      return false;
    }
    const timestamp = Math.max(Math.round(timeMs * 1000), this.lastTimestampUs + 1);
    this.lastTimestampUs = timestamp;
    // Conservative lower bound: never stamp after a slow canvas snapshot.
    const capturedAt = performance.now();
    let frame: VideoFrame;
    try {
      frame = new VideoFrame(this.canvas, { timestamp, alpha: 'discard' });
    } catch (error) {
      this.droppedFrames++;
      this.logger.log('warn', 'render', 'Could not create a video frame from the program canvas', error);
      return false;
    }
    const captureUpperMonotonicMs = performance.now();
    const accepted = this.frameSource?.submit(timestamp, capturedAt, captureUpperMonotonicMs, composition);
    this.pendingSinceMs = performance.now();
    // The generator closes frames it accepts; a rejected write leaves ownership here.
    writer.write(frame).then(
      () => {
        this.pendingSinceMs = -1;
        if (!this.closed && !this.failed) accepted?.();
      },
      (error: unknown) => {
        frame.close();
        if (this.closed || this.failed) return;
        this.failed = true;
        this.logger.log('error', 'render', 'The program track rejected a frame', error);
      },
    );
    return true;
  }

  destroy(): void {
    if (this.closed) return;
    this.closed = true;
    this.frameSource?.close();
    this.writer?.abort().catch(() => undefined);
    this.writer = null;
    this.track.stop();
  }
}
